import React, { useState, useEffect } from 'react';
import { auditLogsAPI, usersAPI } from '../utils/api';
import { Shield, Download, Filter } from 'lucide-react';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    user_id: '',
    entity_type: '',
    action: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const fetchData = async () => {
    try {
      const usersRes = await usersAPI.getAll();
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.action) params.action = filters.action;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const response = await auditLogsAPI.getAll(params);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => new Date(date).toLocaleString();

  const getUserName = (userId) => {
    const user = users.find(u => u._id === userId);
    return user?.full_name || 'Unknown';
  };

  const getActionColor = (action) => {
    const colors = {
      'CREATE': 'bg-green-100 text-green-700',
      'UPDATE': 'bg-blue-100 text-blue-700',
      'DELETE': 'bg-red-100 text-red-700',
      'LOGIN': 'bg-purple-100 text-purple-700',
      'IMPORT': 'bg-orange-100 text-orange-700'
    };
    return colors[action] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Audit Logs</h1>
          <p className="text-slate-500 mt-1">Track all system changes and user activities</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50">
          <Download size={18} />
          Export CSV
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="grid grid-cols-5 gap-4">
          <select
            value={filters.user_id}
            onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Users</option>
            {users.map(u => (
              <option key={u._id} value={u._id}>{u.full_name}</option>
            ))}
          </select>
          <select
            value={filters.entity_type}
            onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Entities</option>
            <option value="deal">Deal</option>
            <option value="client">Client</option>
            <option value="contact">Contact</option>
            <option value="activity">Activity</option>
            <option value="user">User</option>
          </select>
          <select
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Actions</option>
            <option value="CREATE">Create</option>
            <option value="UPDATE">Update</option>
            <option value="DELETE">Delete</option>
            <option value="LOGIN">Login</option>
            <option value="IMPORT">Import</option>
          </select>
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="End Date"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Timestamp</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">User</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Action</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Entity</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log._id} className="border-t border-slate-100">
                  <td className="py-3 px-4 text-sm text-slate-600">{formatDate(log.timestamp)}</td>
                  <td className="py-3 px-4 text-sm text-slate-900">{log.user_name || getUserName(log.user_id)}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${getActionColor(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-900 capitalize">{log.entity_type}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {Object.keys(log.changes || {}).length} field(s) changed
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && logs.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <p>No audit logs found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;